import { BOARD_SIZE, PieceVisual, pieceVisualRenderSizePx } from "./PieceVisual";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useMemo, useState } from "react";

/** Deterministic pseudo-random in [0, 1) from integer seed. */
function unitRandom(seed) {
    const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
    return x - Math.floor(x);
}

/**
 * Top-left positions (px) for each piece index so they sit inside the board-sized tray without die lines.
 */
export function buildScatterPositions(total, grid, templateKey) {
    const cellPx = BOARD_SIZE / grid;
    const pad = cellPx * 0.25;
    const span = Math.max(0, BOARD_SIZE - cellPx - 2 * pad);
    const key = String(templateKey ?? "default");
    let h = 0;
    for (let c = 0; c < key.length; c += 1) h = (h * 31 + key.charCodeAt(c)) | 0;
    const out = {};
    for (let i = 0; i < total; i += 1) {
        const s1 = h + i * 7919 + 1;
        const s2 = h + i * 104729 + 7;
        out[i] = {
            x: pad + unitRandom(s1) * span,
            y: pad + unitRandom(s2) * span,
        };
    }
    return out;
}

const LIST_WIDTH = 100;
const STORAGE_KEY = "jigsaw.scatterViewMode";

function loadViewMode() {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === "list" || saved === "scattered") return saved;
    } catch {}
    return "list";
}

/**
 * Same pool of off-board pieces as {@link CanvasPieces}, but laid out at scattered (x,y) — no die overlay.
 * Drag within the tray to reposition. When shown next to {@link CanvasBoard}, drop on the board to place or drag back here to remove.
 */
export default function CanvasScattered({
    scatteredRef,
    piecesNotOnBoard,
    scatterPositions,
    grid,
    imageUrl,
    paths,
    dragging,
    onScatteredPieceMouseDown,
    /** For QA: overlay each piece with its puzzle grid coords, e.g. (1,0) or (8,8). */
    showPieceGridCoords = true,
    /** Override tray hint (e.g. play page with reference image only, no board). */
    trayDescription = "Scattered — drag pieces anywhere in this tray; drop on the board to place, or here from the board to remove",
    /** Show the scattered/list toggle. Default true for play pages. */
    showViewToggle = false,
    /** When true, pieces are not draggable (admin preview). */
    readOnly = false,
    /**
     * List-view layout: `"column"` (default, vertical strip) or `"row"` (horizontal strip).
     */
    listOrientation = "column",
    /** Force initial view mode (`list` | `scattered`). */
    forceViewMode = null,
    /**
     * Visual scale for list-view pieces (e.g. 2–3 for large grids / fullscreen tray).
     * Does not change board coordinates.
     */
    listPieceScale = 1,
    /**
     * For horizontal list trays: wrap pieces into this many rows (column-major),
     * then scroll horizontally. Default 1.
     */
    listRows = 1,
}) {
    const [viewMode, setViewMode] = useState(() => {
        if (forceViewMode === "list" || forceViewMode === "scattered") return forceViewMode;
        return loadViewMode();
    });
    const listHorizontal = listOrientation === "row";
    const pieceScale = Number.isFinite(Number(listPieceScale)) && Number(listPieceScale) > 0
        ? Number(listPieceScale)
        : 1;
    const rowCount = listHorizontal
        ? Math.max(1, Math.min(6, Math.round(Number(listRows) || 1)))
        : 1;
    const multiRow = listHorizontal && rowCount > 1;
    const nativeCellPx = grid > 0 ? BOARD_SIZE / grid : 0;
    const nativeRenderPx = nativeCellPx > 0 ? pieceVisualRenderSizePx(nativeCellPx) : 48;
    const scaledPiecePx = Math.max(32, Math.round(nativeRenderPx * pieceScale));
    const listGapPx = listHorizontal ? Math.max(12, Math.round(8 * pieceScale)) : 20;
    /** Horizontal list pad (p: 0.5) + 1px border — keep 2-row trays from clipping. */
    const listChromeY = listHorizontal ? 10 : 0;
    const listTrayContentH =
        scaledPiecePx * rowCount + listGapPx * Math.max(0, rowCount - 1);
    const listTrayMaxH = listHorizontal
        ? Math.max(72, listTrayContentH + listChromeY)
        : undefined;

    // Stable list order: each piece gets a fixed rank from its index (not array length),
    // so removing a piece from the tray does not reorder the ones that remain.
    const shuffledPieces = useMemo(() => {
        const seedBase = grid * 31;
        return [...piecesNotOnBoard].sort((a, b) => {
            const ra = unitRandom(seedBase + a * 104729 + 7);
            const rb = unitRandom(seedBase + b * 104729 + 7);
            if (ra !== rb) return ra - rb;
            return a - b;
        });
    }, [piecesNotOnBoard, grid]);

    const handleViewChange = (_, v) => {
        if (!v) return;
        setViewMode(v);
        try { localStorage.setItem(STORAGE_KEY, v); } catch {}
    };

    return (
        <Box
            sx={{
                width:
                    viewMode === "list"
                        ? listHorizontal
                            ? "100%"
                            : { xs: "100%", md: LIST_WIDTH }
                        : BOARD_SIZE,
                transition: "width 0.2s",
            }}
        >
            {(Boolean(trayDescription) || showViewToggle) ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 0.5 }}>
                    <Typography variant="subtitle2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {viewMode === "scattered" ? trayDescription : listHorizontal ? trayDescription : ""}
                    </Typography>
                    {showViewToggle && (
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={handleViewChange}
                            size="small"
                            sx={{ flexShrink: 0, display: { xs: "none", md: "flex" }, "& .MuiToggleButton-root": { px: 0.5, py: 0.25 } }}
                        >
                            <ToggleButton value="scattered" aria-label="Scattered view"><ViewModuleIcon fontSize="small" /></ToggleButton>
                            <ToggleButton value="list" aria-label="List view"><ViewListIcon fontSize="small" /></ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Box>
            ) : null}

            {viewMode === "scattered" ? (
                <Box
                    ref={scatteredRef}
                    sx={{
                        position: "relative",
                        width: BOARD_SIZE,
                        height: BOARD_SIZE,
                        bgcolor: "grey.900",
                        borderRadius: 1,
                        overflow: "visible",
                        display: { xs: "none", md: "block" },
                    }}
                >
                    {piecesNotOnBoard.map((pieceIndex) => {
                        const pos = scatterPositions[pieceIndex];
                        if (!pos) return null;
                        const isDraggingThis = dragging?.pieceIndex === pieceIndex && dragging?.fromScattered;
                        return (
                            <div
                                key={pieceIndex}
                                onMouseDown={
                                    readOnly
                                        ? undefined
                                        : (e) =>
                                              onScatteredPieceMouseDown(e, pieceIndex, {
                                                  scatterX: pos.x,
                                                  scatterY: pos.y,
                                              })
                                }
                                style={{
                                    position: "absolute",
                                    left: pos.x,
                                    top: pos.y,
                                    cursor: readOnly ? "default" : "grab",
                                    pointerEvents: readOnly ? "none" : "auto",
                                    opacity: isDraggingThis ? 0.3 : 1,
                                    zIndex: 1,
                                }}
                            >
                                <PieceVisual
                                    pieceIndex={pieceIndex}
                                    imageUrl={imageUrl}
                                    grid={grid}
                                    paths={paths}
                                    showGridCoords={showPieceGridCoords}
                                />
                            </div>
                        );
                    })}
                </Box>
            ) : null}
            {/* List view: always on mobile, on desktop only when viewMode is list */}
            <Box
                ref={viewMode === "list" ? scatteredRef : undefined}
                sx={{
                    width: listHorizontal ? "100%" : { xs: "100%", md: LIST_WIDTH },
                    ...(listHorizontal && multiRow
                        ? {
                              height: listTrayMaxH,
                              maxHeight: listTrayMaxH,
                          }
                        : {
                              maxHeight: listHorizontal
                                  ? listTrayMaxH
                                  : { xs: 120, md: BOARD_SIZE },
                          }),
                    boxSizing: "border-box",
                    overflowY: listHorizontal ? "hidden" : { xs: "hidden", md: "auto" },
                    overflowX: listHorizontal ? "auto" : { xs: "auto", md: "hidden" },
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 0.5,
                    display: viewMode === "scattered" ? { xs: "flex", md: "none" } : multiRow ? "grid" : "flex",
                    ...(multiRow
                        ? {
                              gridTemplateRows: `repeat(${rowCount}, ${scaledPiecePx}px)`,
                              gridAutoFlow: "column",
                              gridAutoColumns: `${scaledPiecePx}px`,
                              alignItems: "center",
                              justifyItems: "center",
                              gap: `${listGapPx}px`,
                          }
                        : {
                              flexDirection: listHorizontal
                                  ? "row"
                                  : { xs: "row", md: "column" },
                              alignItems: listHorizontal ? "center" : "stretch",
                              gap: listHorizontal ? `${listGapPx}px` : "20px",
                          }),
                }}
            >
                {shuffledPieces.map((pieceIndex) => {
                    const isDraggingThis = dragging?.pieceIndex === pieceIndex && dragging?.fromScattered;
                    const startListDrag = readOnly
                        ? undefined
                        : (e) => {
                              const elRect = e.currentTarget.getBoundingClientRect();
                              const parentRect = scatteredRef.current?.getBoundingClientRect();
                              const scatterX = parentRect ? elRect.left - parentRect.left : 0;
                              const scatterY = parentRect ? elRect.top - parentRect.top : 0;
                              onScatteredPieceMouseDown(e, pieceIndex, {
                                  scatterX,
                                  scatterY,
                                  listTray: true,
                              });
                          };
                    return (
                        <Box
                            key={pieceIndex}
                            sx={{
                                cursor: readOnly ? "default" : "grab",
                                pointerEvents: readOnly ? "none" : "auto",
                                opacity: isDraggingThis ? 0.3 : 1,
                                flexShrink: 0,
                                width: pieceScale !== 1 ? scaledPiecePx : undefined,
                                height: pieceScale !== 1 ? scaledPiecePx : undefined,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                touchAction: readOnly ? "auto" : "none",
                                userSelect: "none",
                            }}
                            onPointerDown={startListDrag}
                        >
                            <Box
                                sx={
                                    pieceScale !== 1
                                        ? {
                                              transform: `scale(${pieceScale})`,
                                              transformOrigin: "center center",
                                          }
                                        : undefined
                                }
                            >
                                <PieceVisual
                                    pieceIndex={pieceIndex}
                                    imageUrl={imageUrl}
                                    grid={grid}
                                    paths={paths}
                                    showGridCoords={showPieceGridCoords}
                                    anchorToCell={false}
                                />
                            </Box>
                        </Box>
                    );
                })}
                {shuffledPieces.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", display: "block", py: 2 }}>
                        All pieces placed
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
